import LogRocket from 'logrocket'
import setupLogRocketReact from 'logrocket-react'
import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router'
import { useLocation } from 'react-router-dom'

import { EuiFlexGroup, EuiFlexItem, EuiLoadingChart, EuiSpacer } from '@elastic/eui'

import { getCookie } from 'api/adcritterApi'
import { useGetCanSeeBundleInsightsQuery } from 'api/rtkQueryApi/platform/bundleInsightsApi'
import { useGetCampaignsQuery } from 'api/rtkQueryApi/platform/campaignsApi'
import { usePostTouchDashboardMutation } from 'api/rtkQueryApi/platform/dashboardApi'
import { useLazyGetDomainsQuery } from 'api/rtkQueryApi/platform/domainsApi'
import { agencySpecialMatchingOffer } from 'app/appSlice'
import AcLoadingChart from 'components/AcLoadingChart'
import PrivateRoute from 'components/PrivateRoute'
import { LoggingUserInPage } from 'components/authentication/loggingUserInPage'
import { AuthenticatorRoutes } from 'components/authentication/useAuthenticationSwitch'
import AudiencePixelPage from 'features/accounts/AudiencePixelPage'
import { CreateClientPage } from 'features/accounts/CreateClientPage'
import CreatePage from 'features/accounts/CreatePage'
import { ShopifyTrackingPage } from 'features/accounts/ShopifyTrackingPage'
import BuildersPage from 'features/builders/BuildersPage'
import BundleInsightsPage from 'features/bundleDashboard/BundleInsightsPage'
import CampaignsPage from 'features/campaigns/CampaignsPage'
import MasterCampaignRoutes from 'features/campaigns/masterCampaigns/MasterCampaignRoutes'
import GotoAccountPage from 'features/choose/GotoAccountPage'
import GotoAdPage from 'features/choose/GotoAdPage'
import GotoCampaignPage from 'features/choose/GotoCampaignPage'
import GotoOrganizationPage from 'features/choose/GotoOrganizationPage'
import DashboardPage from 'features/dashboard/DashboardPage'
import InvoicesPage from 'features/invoices/InvoicesPage'
import { AgencyOffer } from 'features/offers/AgencyOffer'
import { AgencyOfferAccepted } from 'features/offers/AgencyOfferAccepted'
import { OfferPanel } from 'features/offers/OfferPanel'
import ForgotPage from 'features/outside/forgot/ForgotPage'
import LoginPage from 'features/outside/login/LoginPage'
import RefreshTokenLoginPage from 'features/outside/login/RefreshTokenLoginPage'
import RestartPage from 'features/outside/restart/RestartPage'
import VistaSignUpPage from 'features/outside/vista/VistaSignUpPage'
import LegacyReportsPage from 'features/reports/LegacyReportsPage'
import ReportsRouter from 'features/reports/ReportsRouter'
import ShopifyAuthenticatePage from 'features/shopify/ShopifyAuthenticatePage'
import ShopifyEntryPage from 'features/shopify/ShopifyEntryPage'
import { CampaignAdsViewer } from 'features/viewers/CampaignAdsViewer'
import { CampaignApprovalQueuePage } from 'features/approvals/CampaignApprovalQueuePage'
import { useCampaignApproval } from 'hooks/useCampaignApproval'
import { useEnterpriseSupport } from 'hooks/useEnterpriseSupport'
import { useWhiteLabel } from 'whiteLabel/WhiteLabelContext'

import { PermissionType, WhiteLabel } from '../api/interfaces'
import { ClientDetailsPage } from '../features/accounts/ClientDetailsPage'
import SettingsPage from '../features/accounts/SettingsPage'
import { QuickCampaignPage } from '../features/builders/agency/QuickCampaignPage'
import { SimpleCampaignPage } from '../features/builders/agency/SimpleCampaignPage'
import { ClientPickerPage } from '../features/clients/ClientPickerPage'
import { InventoryExplorerPage } from '../features/inventoryExplorer/InventoryExplorerPage'
import { AiRoutes } from '../features/planner/AiRoutes'
import { UserInviteAccept } from '../features/users/UserInviteAccept'
import { useOrganizationPermissions } from '../providers/OrganizationPermissionsContext'
import { RootState } from './rootReducer'

const Routes: React.FC = () => {
  const dispatch = useDispatch()
  const isLogRocketSetup = useRef(false)
  const { isLoggedIn, isInSignupProcess, isShopifyUser, currentAccount, currentOrganization, currentUser, isStaffUser } = useSelector((state: RootState) => state.app)
  const whiteLabel = useWhiteLabel()
  const location = useLocation()
  const search = new URLSearchParams(location.search)
  const { screenSize } = useSelector((state: RootState) => state.events)
  const { hasPermission } = useOrganizationPermissions()
  const { isEnabled: campaignApprovalEnabled, canCreate: canCreateCampaigns, canApprove } = useCampaignApproval()
  const isEnterpriseOrganization = useEnterpriseSupport()

  useEffect(() => {
    if (search.get('agencySpecialMatchingOffer') === 'true') {
      dispatch(agencySpecialMatchingOffer())
    }
  }, [dispatch, search])

  const { isFetching: isFetchingCampaigns } = useGetCampaignsQuery(
    {
      accountId: currentAccount?.id ?? '',
      isAgency: whiteLabel?.isAgencies() ?? false
    },
    { skip: !currentAccount || !whiteLabel }
  )
  const { isFetching: isFetchingCanSeeBundleInsights, data: canSeeBundleInsights } = useGetCanSeeBundleInsightsQuery({ accountId: currentAccount?.id ?? '' }, { skip: !currentAccount || !currentOrganization })
  const [postTouchDashboard, postTouchDashboardResult] = usePostTouchDashboardMutation()

  const [triggerGetDomains, domainsResult] = useLazyGetDomainsQuery()

  useEffect(() => {
    if (!isLoggedIn || !currentOrganization || !whiteLabel) {
      return
    }

    triggerGetDomains()
  }, [isLoggedIn, currentOrganization, whiteLabel])

  useEffect(() => {
    if (!domainsResult?.data || domainsResult.data.length <= 0) {
      return
    }

    for (const domain of domainsResult.data) {
      new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(domain.logoUrl)
        img.onerror = () => reject()

        img.src = domain.logoUrl
      })
    }
  }, [domainsResult])

  useEffect(() => {
    if (!isLoggedIn || !currentOrganization || !whiteLabel || postTouchDashboardResult.isLoading || postTouchDashboardResult.data !== undefined) {
      return
    }

    postTouchDashboard({
      clck: getCookie('_clck'),
      isAgency: whiteLabel.isAgencies()
    })
  }, [isLoggedIn, currentOrganization, whiteLabel, postTouchDashboard])

  useEffect(() => {
    if (isLogRocketSetup.current || !isLoggedIn || !currentOrganization) {
      return
    }

    if (process.env.NODE_ENV === 'production' && !isStaffUser) {
      if (currentOrganization.whiteLabel === WhiteLabel.AgencyV2) {
        LogRocket.init('g51ouz/agencyv2')
      } else {
        LogRocket.init('g51ouz/platform-y7rph')
      }

      setupLogRocketReact(LogRocket)
      isLogRocketSetup.current = true
    }
  }, [isLoggedIn, isStaffUser, currentOrganization])

  useEffect(() => {
    if (!isLogRocketSetup.current || !currentOrganization || !currentUser) {
      return
    }

    LogRocket.identify(currentUser.email, {
      name: currentUser.name,
      email: currentUser.email,
      organizationId: currentOrganization.id,
      organizationName: currentOrganization.name
    })
  }, [isLogRocketSetup, currentOrganization, currentUser, isStaffUser])

  useEffect(() => {
    if (!currentOrganization || !postTouchDashboardResult.data) {
      return
    }

    let clarity = (window as any)['clarity']
    if (typeof clarity == 'function') {
      clarity('upgrade', 'dashboard login date updated')
      clarity('set', 'organization_id', currentOrganization.id)
    }
  }, [currentOrganization, postTouchDashboardResult])

  return (
    <Switch>
      <Route
        exact
        path={['/']}
        render={() => {
          if (!isLoggedIn) {
            return <Redirect to='/login' />
          }

          if (isFetchingCanSeeBundleInsights || isFetchingCampaigns) {
            if (screenSize.sm) {
              return (
                <EuiFlexGroup justifyContent='center' responsive={false}>
                  <EuiFlexItem grow={false}>
                    <EuiSpacer size='l' />
                    <EuiLoadingChart size='xl' />
                  </EuiFlexItem>
                </EuiFlexGroup>
              )
            } else {
              return <AcLoadingChart />
            }
          }

          if (screenSize.sm) {
            return <Redirect to='/dashboard' />
          }

          return canSeeBundleInsights ? <Redirect to='/planInsights' /> : <Redirect to='/dashboard' />
        }}
      />
      <Route exact={true} path='/login'>
        <LoginPage />
      </Route>
      <Route exact={true} path='/token_login'>
        <RefreshTokenLoginPage />
      </Route>
      <Route exact={true} path={`/${AuthenticatorRoutes.Vendasta}`}>
        <LoggingUserInPage />
      </Route>
      <Route exact={true} path='/signup'>
        {whiteLabel?.isVista() && <VistaSignUpPage />}
      </Route>
      <Route exact={true} path='/shopify/signup'>
        <ShopifyEntryPage />
      </Route>
      <Route exact={true} path='/shopify'>
        <ShopifyAuthenticatePage />
      </Route>
      <Route exact={true} path='/forgot'>
        <ForgotPage />
      </Route>
      <Route exact={true} path='/adViewer/:campaignId'>
        <CampaignAdsViewer />
      </Route>
      <Route exact={true} path='/userInvite'>
        <UserInviteAccept />
      </Route>

      <PrivateRoute path='/restart'>
        <RestartPage />
      </PrivateRoute>

      {whiteLabel?.getWhiteLabel() === WhiteLabel.AgencyV2 && (
        <PrivateRoute path={`/ai`}>
          <AiRoutes />
        </PrivateRoute>
      )}
      {whiteLabel?.getWhiteLabel() === WhiteLabel.AgencyV2 && (
        <PrivateRoute path={`/masterCampaigns`}>
          <MasterCampaignRoutes />
        </PrivateRoute>
      )}
      {whiteLabel?.getWhiteLabel() === WhiteLabel.AgencyV2 && (
        <PrivateRoute exact={true} path={`/inventoryExplorer`}>
          <InventoryExplorerPage />
        </PrivateRoute>
      )}

      {whiteLabel?.getWhiteLabel() === WhiteLabel.AgencyV2 && (
        <PrivateRoute exact={true} path={`/create-client`}>
          <CreateClientPage />
        </PrivateRoute>
      )}

      {isInSignupProcess && (
        <PrivateRoute path='/build'>
          <BuildersPage />
        </PrivateRoute>
      )}
      {!isInSignupProcess && (
        <React.Fragment>
          <PrivateRoute path='/dashboard'>
            <DashboardPage />
          </PrivateRoute>
          <PrivateRoute path='/offers/accepted' exact>
            <OfferPanel>
              <AgencyOfferAccepted />
            </OfferPanel>
          </PrivateRoute>
          <PrivateRoute path='/offers' exact>
            <OfferPanel>
              <AgencyOffer />
            </OfferPanel>
          </PrivateRoute>
          <PrivateRoute path='/planInsights'>
            <BundleInsightsPage />
          </PrivateRoute>

          {whiteLabel?.getWhiteLabel() === WhiteLabel.AgencyV2 ? (
            <PrivateRoute path={'/client-details'}>
              <ClientDetailsPage />
            </PrivateRoute>
          ) : (
            <PrivateRoute path={'/settings'}>
              <SettingsPage />
            </PrivateRoute>
          )}

          <PrivateRoute path='/audience-pixel'>{isShopifyUser ? <ShopifyTrackingPage /> : <AudiencePixelPage />}</PrivateRoute>

          <PrivateRoute path='/gotoAd/:organizationId/:accountId/:campaignId/:adId'>
            <GotoAdPage />
          </PrivateRoute>
          <PrivateRoute path='/gotoCampaign/:organizationId/:accountId/:campaignId'>
            <GotoCampaignPage />
          </PrivateRoute>
          <PrivateRoute path='/gotoCampaignInsights/:organizationId/:accountId/:campaignId'>
            <GotoCampaignPage isInsights={true} />
          </PrivateRoute>
          <PrivateRoute path='/gotoAccount/:organizationId/:accountId'>
            <GotoAccountPage />
          </PrivateRoute>
          <PrivateRoute path='/gotoOrganization/:organizationId'>
            <GotoOrganizationPage />
          </PrivateRoute>

          <PrivateRoute path='/newAccount'>
            <CreatePage />
          </PrivateRoute>

          <PrivateRoute path='/build'>{campaignApprovalEnabled && !canCreateCampaigns ? <Redirect to='/campaigns' /> : whiteLabel?.getWhiteLabel() === WhiteLabel.AgencyV2 ? <SimpleCampaignPage /> : <BuildersPage />}</PrivateRoute>
          {(isStaffUser || isEnterpriseOrganization) && (
            <PrivateRoute path={`/quickCampaign`} exact>
              {campaignApprovalEnabled && !canCreateCampaigns ? <Redirect to='/campaigns' /> : <QuickCampaignPage />}
            </PrivateRoute>
          )}
          {hasPermission(PermissionType.UseTemplateBuilderCampaignCreation) && (
            <PrivateRoute path='/templateBuild'>{campaignApprovalEnabled && !canCreateCampaigns ? <Redirect to='/campaigns' /> : <BuildersPage />}</PrivateRoute>
          )}
          <PrivateRoute path='/campaigns'>
            <CampaignsPage />
          </PrivateRoute>
          {campaignApprovalEnabled && canApprove && (
            <PrivateRoute exact path='/approvals'>
              <CampaignApprovalQueuePage />
            </PrivateRoute>
          )}
          <PrivateRoute path='/legacy-reports'>
            <LegacyReportsPage />
          </PrivateRoute>
          <PrivateRoute path='/reports'>
            <ReportsRouter />
          </PrivateRoute>

          <PrivateRoute path='/invoices'>
            <InvoicesPage />
          </PrivateRoute>
          {whiteLabel?.getWhiteLabel() === WhiteLabel.AgencyV2 && (
            <PrivateRoute path={'/clients'}>
              <ClientPickerPage />
            </PrivateRoute>
          )}
        </React.Fragment>
      )}
    </Switch>
  )
}

export default Routes
